/**
 * @module app.cm
 */

import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import { HouseholdSelect } from 'app/cd/components/household/household-select';
import { ManageCasesPageContext } from 'app/cm/lib/manage-cases-page-context';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { openHouseholdCaseSummary } from 'app/cm/components/summary/household-case-summary';
import { HouseholdService, IHouseholdSearchFilters } from 'app/cd/services/household-service';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { CaseService } from 'app/cm/services/case-service';
import { validate } from 'app/shared/elements/ess-rule';
import { getReference } from 'app/shared/models/essette-types';
import { IUserSearchFilters, UserService } from 'app/core/services/user-service';
import { guid } from 'app/shared/lib/guid';

interface IHouseholdCaseCreateFields {
    assignTo: CM.CaseOwner | null;
    programGroup: CM.ProgramGroup | null;
    program: CM.Program | null;
    acuity: CM.Acuity | null;
    intensity: CM.Intensity | null;
}

@autoinject()
export class CreateHouseholdCasePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @observable
    public household: CD.Household | null = null;

    private get householdId(): string | null {
        return (this.household && this.household.id) || null;
    }

    protected forceHouseholdSearch: boolean = false;
    protected hasHouseholdCaseRoute: boolean = false;
    protected showHouseholdCaseSummaryLink: boolean = false;


    protected id: guid = guid();

    //#region Existing Household Cases
    protected householdCaseResults: CM.HouseholdCase[] = [];
    private sorting = new Sorting();
    protected pageSize: number = 5;
    private resultCount: number = 0;
    //#endregion Existing Household Cases

    //#region Household Case Creation
    protected validate = validate;
    protected isValid: boolean = false;
    public caseCreated: boolean = false;

    protected users: CM.CaseOwner[] = [];
    protected programGroups: CM.ProgramGroup[] | null = null;
    protected acuities: CM.Acuity[] = [];
    protected intensities: CM.Intensity[] = [];
    public createFields: IHouseholdCaseCreateFields = {
        assignTo: null,
        programGroup: null,
        program: null,
        acuity: null,
        intensity: null
    };

    protected openHouseholdCaseSummary = openHouseholdCaseSummary;

    //#endregion Household Case Creation

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $householdSelect: HouseholdSelect | null = null;
    public $referralForm: HTMLElement | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: ManageCasesPageContext,
        private readonly householdService: HouseholdService,
        private readonly caseService: CaseService,
        private readonly userService: UserService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(params: { householdId: string, searchMode?: boolean }): Promise<void> {
        this.checkPermissions();
        this.loadLookups();

        if(params.householdId && params.householdId !== this.householdId) {
            this.household = await this.loadHousehold(params.householdId);
        } else {
            this.household = null;
        }

        if(params.searchMode) {
            this.forceHouseholdSearch = true;
        }
    }

    protected attached(): void {
        if(this.$householdSelect) {
            if(this.forceHouseholdSearch) {
                this.$householdSelect.search(true);
            }
            if(this.household) {
                this.$householdSelect.selectHousehold(this.household);
            }
        }
    }

    ////****************** Events ***************************************************////

    // #region Household Case Table
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.searchHouseholdCases();
    }
    // #endregion Household Case Table

    protected householdChanged(newHousehold: CD.Household | null, oldHousehold: CD.Household | null): void {
        this.searchHouseholdCases();
    }

    protected householdSearchFiltersChanged(newFilters: IHouseholdSearchFilters, oldFilters: IHouseholdSearchFilters): void {
        this.context.householdSearchFilters = newFilters;
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasHouseholdCaseRoute = this.accountContext.hasRouteAccess('cm.household-case')
        this.showHouseholdCaseSummaryLink = this.accountContext.hasRouteAccess('cm.household-case.summary');
    }

    // Reset fields back to default values
    private reset(): void {
        this.household = null;
        this.householdCaseResults = [];
        this.sorting = new Sorting();
        this.resultCount = 0;
        this.caseCreated = false;
        if(this.$pager) this.$pager.updatePage(0);
        this.createFields = {
            assignTo: null,
            programGroup: null,
            program: null,
            acuity: null,
            intensity: null
        };
        if(this.$householdSelect?.reset) {
            this.$householdSelect.reset();
        }
    }

    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const userSearchFilters: IUserSearchFilters = {
                notInternal: true,
                hasNoExternalSystem: true
            }

            const options: StandardLoadOptions = { all: true };
            const [users, programGroups, acuities, intensities] = await Promise.all(
                [
                    this.userService.loadUsers(options, [], userSearchFilters),
                    this.caseService.loadProgramGroups(options, ['programs'], false, false, true),
                    this.caseService.loadAcuities(options),
                    this.caseService.loadIntensities(options)
                ]
            );

            this.users = users.results;
            this.programGroups = programGroups.results;
            this.acuities = acuities.results;
            this.intensities = intensities.results;
        }
        catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async searchHouseholdCases(): Promise<void> {
        if(!this.household?.id) {
            this.householdCaseResults = [];
            this.resultCount = 0;
            return;
        }

        try {
            this.state.beginLoad();

            const options: StandardLoadOptions = {
                sorting: this.sorting,
                top: this.pageSize,
                page: this.$pager?.page || 0,
                includeInactive: false
            };

            const includes: string[] = [
                'assignedTo',
                'program',
                'household',
                'metadata.created.byUser'
            ];

            const response = await this.caseService.searchHouseholdCases({ householdId: this.household.id }, options, includes);
            this.householdCaseResults = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadHousehold(householdId: string): Promise<CD.Household> {
        try {
            this.state.beginLoad();

            const response = await this.householdService.loadHousehold(householdId);
            return response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async createHouseholdCase(openCase: boolean): Promise<void> {
        if(!this.isValid || this.caseCreated) return;

        const newHouseholdCase: CM.HouseholdCase = {
            household: getReference(this.household),
            assignedTo: getReference(this.createFields && this.createFields.assignTo),
            program: !!this.createFields.programGroup ? getReference(this.createFields.program) : undefined,
            acuity: getReference(this.createFields.acuity),
            intensity: getReference(this.createFields.intensity)
        }

        try {
            this.state.beginSave();

            const response = await this.caseService.insertHouseholdCase(newHouseholdCase);
            this.caseCreated = response.isSuccessful;
            if(response.isSuccessful && response.id) {
                if(openCase && this.hasHouseholdCaseRoute) {
                    this.router.navigateToRoute('cm.household-case', { householdCaseId: response.id });
                } else {
                    this.reset();
                    this.router.navigateToRoute('cm.manage-cases.household.create');
                }
            } else {
                throw new Error(
                    'API Not Successful'
                );
            }
        }
        catch(error) {
            this.toaster.standard.created('cm:householdCase.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
